/**
 * Store App — Customer-facing storefront
 * Separate from the admin panel which runs as its own app.
 */
import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from '@/hooks/useAuth'
import { isRTL } from '@/lib/i18n'
import { PageLoader } from '@/components/ui/PageLoader'

// ─── Layouts ──────────────────────────────────────────────────────────────────
import StorefrontLayout from '@/components/layout/StorefrontLayout'

// ─── Auth pages ───────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const EmailVerificationPage = lazy(() => import('@/pages/auth/EmailVerificationPage'))
const CheckEmailPage = lazy(() => import('@/pages/auth/CheckEmailPage'))

// ─── Storefront pages ─────────────────────────────────────────────────────────
const HomePage = lazy(() => import('@/pages/storefront/HomePage'))
const ProductListingPage = lazy(() => import('@/pages/storefront/ProductListingPage'))
const ProductDetailPage = lazy(() => import('@/pages/storefront/ProductDetailPage'))
const CartPage = lazy(() => import('@/pages/storefront/CartPage'))
const SearchResultsPage = lazy(() => import('@/pages/storefront/SearchResultsPage'))
const CheckoutPage = lazy(() => import('@/pages/storefront/CheckoutPage'))
const BarcodeScannerPage = lazy(() => import('@/pages/storefront/BarcodeScannerPage'))
const OAuthCallbackPage = lazy(() => import('@/pages/auth/OAuthCallbackPage'))

// ─── Account pages ────────────────────────────────────────────────────────────
const AccountLayout = lazy(() => import('@/pages/account/AccountLayout'))
const ProfilePage = lazy(() => import('@/pages/account/ProfilePage'))
const AddressesPage = lazy(() => import('@/pages/account/AddressesPage'))
const OrderHistoryPage = lazy(() => import('@/pages/account/OrderHistoryPage'))
const OrderDetailPage = lazy(() => import('@/pages/account/OrderDetailPage'))
const WishlistPage = lazy(() => import('@/pages/account/WishlistPage'))
const LoyaltyPage = lazy(() => import('@/pages/account/LoyaltyPage'))
const ReturnsPage = lazy(() => import('@/pages/account/ReturnsPage'))

// ─── POS ──────────────────────────────────────────────────────────────────────
const POSPage = lazy(() => import('@/pages/pos/POSPage'))
const POSLoginPage = lazy(() => import('@/pages/pos/POSLoginPage'))
const POSTakeoverPage = lazy(() => import('@/pages/pos/POSTakeoverPage'))
const POSGuard = lazy(() => import('@/pages/pos/POSGuard'))
const PosOrderBillPage = lazy(() => import('@/pages/pos/PosOrderBillPage'))

// ─── CMS Static Pages ─────────────────────────────────────────────────────────
const StaticPageView = lazy(() => import('@/pages/storefront/StaticPageView'))
const ContactPage = lazy(() => import('@/pages/storefront/ContactPage'))
const BlogListPage = lazy(() => import('@/pages/storefront/BlogListPage'))
const BlogPostPage = lazy(() => import('@/pages/storefront/BlogPostPage'))
const SchemesListPage = lazy(() => import('@/pages/storefront/SchemesListPage'))
const SchemeDetailPage = lazy(() => import('@/pages/storefront/SchemeDetailPage'))
const MySchemesPage = lazy(() => import('@/pages/account/MySchemesPage'))
const SchemeEnrollmentPage = lazy(() => import('@/pages/account/SchemeEnrollmentPage'))

// ─── Plugin Guard ─────────────────────────────────────────────────────────────
import { PluginGuard } from '@/components/PluginGuard'
import { PLUGIN_IDS } from '@shared/plugins/registry'

// ─── 404 ──────────────────────────────────────────────────────────────────────
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

/** Preserves ?token= and ?email= when redirecting old email URLs. */
function LegacyAuthRedirect({ to }: { to: string }) {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} replace />
}

// ─── Loading fallback ─────────────────────────────────────────────────────────
// Removed inline PageLoader — now using dedicated component imported at the top

// ─── RTL handler ──────────────────────────────────────────────────────────────
function RTLHandler() {
  const { i18n } = useTranslation()

  useEffect(() => {
    const rtl = isRTL(i18n.language)
    document.documentElement.dir = rtl ? 'rtl' : 'ltr'
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  return null
}

// ─── Auth initializer ─────────────────────────────────────────────────────────
function AuthInitializer() {
  const { pathname } = useLocation()
  // POS uses its own session (pos-access-token). Storefront auth/me must not clear it.
  useCurrentUser({ enabled: !pathname.startsWith('/pos') })
  return null
}

// ─── App ──────────────────────────────────────────────────────────────────────

function App() {
  return (
    <>
      <RTLHandler />
      <AuthInitializer />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── POS routes ── */}
          <Route path="/pos/takeover" element={<POSTakeoverPage />} />
          <Route path="/pos/login" element={<POSLoginPage />} />
          <Route path="/pos" element={<POSGuard />}>
            <Route index element={<POSPage />} />
            <Route path="orders/:orderId/bill" element={<PosOrderBillPage />} />
          </Route>

          {/* ── Storefront routes (with header/footer) ── */}
          <Route element={<StorefrontLayout />}>
            {/* Auth routes */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/register" element={<RegisterPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/verify-email" element={<EmailVerificationPage />} />
            <Route path="/auth/check-email" element={<CheckEmailPage />} />
            {/* Legacy email link paths (preserve query string) */}
            <Route path="/reset-password" element={<LegacyAuthRedirect to="/auth/reset-password" />} />
            <Route path="/verify-email" element={<LegacyAuthRedirect to="/auth/verify-email" />} />
            <Route path="/auth/oauth/callback" element={<OAuthCallbackPage />} />
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductListingPage />} />
            <Route path="/products/:slug" element={<ProductDetailPage />} />
            <Route path="/categories" element={<ProductListingPage />} />
            <Route path="/categories/:slug" element={<ProductListingPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/scan" element={<BarcodeScannerPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />

            <Route path="/savings-schemes" element={<PluginGuard pluginId={PLUGIN_IDS.SAVINGS_SCHEMES}><SchemesListPage /></PluginGuard>} />
            <Route path="/savings-schemes/:slug" element={<PluginGuard pluginId={PLUGIN_IDS.SAVINGS_SCHEMES}><SchemeDetailPage /></PluginGuard>} />

            {/* Account */}
            <Route path="/account" element={<AccountLayout />}>
              <Route index element={<Navigate to="/account/profile" replace />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="addresses" element={<AddressesPage />} />
              <Route path="orders" element={<OrderHistoryPage />} />
              <Route path="orders/:id" element={<OrderDetailPage />} />
              <Route path="wishlist" element={<PluginGuard pluginId={PLUGIN_IDS.WISHLIST} requireFeature="enable_wishlist"><WishlistPage /></PluginGuard>} />
              <Route path="loyalty" element={<PluginGuard pluginId={PLUGIN_IDS.LOYALTY}><LoyaltyPage /></PluginGuard>} />
              <Route path="returns" element={<PluginGuard pluginId={PLUGIN_IDS.RMA}><ReturnsPage /></PluginGuard>} />
              <Route path="savings-schemes" element={<PluginGuard pluginId={PLUGIN_IDS.SAVINGS_SCHEMES}><MySchemesPage /></PluginGuard>} />
              <Route path="savings-schemes/:id" element={<PluginGuard pluginId={PLUGIN_IDS.SAVINGS_SCHEMES}><SchemeEnrollmentPage /></PluginGuard>} />
            </Route>

            {/* Contact page */}
            <Route path="/contact" element={<PluginGuard pluginId={PLUGIN_IDS.CONTACT_FORM}><ContactPage /></PluginGuard>} />

            {/* Blog */}
            <Route path="/blog" element={<PluginGuard pluginId={PLUGIN_IDS.BLOG}><BlogListPage /></PluginGuard>} />
            <Route path="/blog/:slug" element={<PluginGuard pluginId={PLUGIN_IDS.BLOG}><BlogPostPage /></PluginGuard>} />

            {/* CMS Static Pages (low priority, catch-all for slugs) */}
            <Route path="/:slug" element={<PluginGuard pluginId={PLUGIN_IDS.STATIC_PAGES}><StaticPageView /></PluginGuard>} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}

export default App
